import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, ListOrdered, MoreVertical, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const CLASSES = [1, 2, 3, 4, 5, 6, 7, 8];
const TYPES = [
  { value: "actif", label: "Actif" }, { value: "passif", label: "Passif" },
  { value: "tiers", label: "Tiers" }, { value: "tresorerie", label: "Trésorerie" },
  { value: "charge", label: "Charge" }, { value: "produit", label: "Produit" },
];

export default function ChartOfAccounts() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState(0);

  const load = () => api.get("/accounting/accounts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const schema = [
    { name: "numero", label: "Numéro de compte", required: true },
    { name: "libelle", label: "Libellé", required: true, col: 2 },
    { name: "classe", label: "Classe", type: "select", options: CLASSES.map((c) => ({ value: c, label: `Classe ${c}` })) },
    { name: "type", label: "Type", type: "select", options: TYPES },
  ];

  const save = async (form) => {
    const payload = { ...form, classe: Number(form.classe) || Number(String(form.numero)[0]) || 1 };
    try {
      if (editing) await api.put(`/accounting/accounts/${editing.id}`, payload);
      else await api.post("/accounting/accounts", payload);
      toast.success(editing ? "Compte mis à jour" : "Compte créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce compte ?")) return; await api.delete(`/accounting/accounts/${id}`); toast.success("Supprimé"); load(); };

  const filtered = (items || []).filter((a) => (!cls || a.classe === cls) && (!q || `${a.numero} ${a.libelle}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <PageHeader title="Plan comptable" subtitle="Comptes SYSCOHADA révisé (classes 1 à 8)">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-account" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau compte</Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative max-w-xs flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un compte…" className="pl-9" data-testid="account-filter" /></div>
        <div className="flex gap-1 flex-wrap">
          <Button size="sm" variant={cls === 0 ? "default" : "outline"} onClick={() => setCls(0)} className={cls === 0 ? "bg-brand-600 text-white" : ""}>Toutes</Button>
          {CLASSES.map((c) => <Button key={c} size="sm" variant={cls === c ? "default" : "outline"} onClick={() => setCls(c)} data-testid={`class-filter-${c}`} className={cls === c ? "bg-brand-600 text-white" : ""}>{c}</Button>)}
        </div>
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={ListOrdered} title="Aucun compte" subtitle="Ajoutez un compte au plan comptable." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N° compte</TableHead><TableHead>Libellé</TableHead><TableHead>Classe</TableHead><TableHead>Type</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`account-row-${a.numero}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-sm font-semibold text-brand-700">{a.numero}</TableCell>
                  <TableCell>{a.libelle}</TableCell>
                  <TableCell className="text-slate-500">Classe {a.classe}</TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{a.type}</span></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`account-menu-${a.numero}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(a); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(a.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le compte" : "Nouveau compte"} schema={schema} initial={editing || { classe: 1, type: "actif" }} onSubmit={save} />
    </div>
  );
}
